<template>
    <v-menu v-model="snapshotMenu" offset-x v-if="showSnapshotMenu" nudge-top="8" :close-on-content-click="false" min-width="300">
        <template v-slot:activator="{ on: menuOn }">
            <v-tooltip right :disabled="!collapsed">
                <template v-slot:activator="{ on: tooltipOn }">
                    <v-list-item
                        v-on="{ ...menuOn, ...tooltipOn }"
                        @mouseenter="isMenuActivatorHovered = true"
                        @mouseleave="isMenuActivatorHovered = false"
                        :disabled="fetchingSnapshots"
                        :input-value="Boolean(activeSnapshotSnid)"
                        active-class="primary--text">
                        <v-list-item-icon>
                            <v-icon :color="selectedSnapshotIndicator.color">
                                {{ isMenuActivatorHovered ? 'mdi-chevron-right' : selectedSnapshotIndicator.icon }}
                            </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content v-if="fetchingSnapshots">
                            <v-progress-linear indeterminate rounded></v-progress-linear>
                        </v-list-item-content>
                        <v-list-item-content v-else>
                            <v-list-item-title :class="{ 'text--disabled': isSpaceRested || isSpaceArchived }">
                                <b>{{ snapshotName }}</b>
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="showQuickActions && !collapsed && isInstanceEditor" class="my-0">
                            <v-menu v-model="snapshotActionsMenu" offset-x right nudge-top="10">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                        <v-icon small>more_horiz</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense nav>
                                    <snapshot-restore-dialog
                                        v-if="quickActionSnapshot && canRestoreToCurrent"
                                        :snapshotData="quickActionSnapshot"
                                        :isRestoreToCurrentInstance="true">
                                        <template v-slot:activator="{ attrs, open }">
                                            <v-list-item v-bind="attrs" @click.stop="open">
                                                <v-list-item-icon>
                                                    <v-icon>restore</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="mr-4">Restore to current state</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </snapshot-restore-dialog>
                                    <v-list-item v-else disabled>
                                        <v-list-item-icon>
                                            <v-icon>restore</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Restore to current state</v-list-item-title>
                                    </v-list-item>

                                    <snapshot-restore-dialog
                                        v-if="quickActionSnapshot && canRestoreToAnother"
                                        :snapshotData="quickActionSnapshot"
                                        :isRestoreToCurrentInstance="false">
                                        <template v-slot:activator="{ attrs, open }">
                                            <v-list-item v-bind="attrs" @click.stop="open">
                                                <v-list-item-icon>
                                                    <v-icon>group</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="mr-4">Restore to another instance</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </snapshot-restore-dialog>
                                    <v-list-item v-else disabled>
                                        <v-list-item-icon>
                                            <v-icon>group</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Restore to another instance</v-list-item-title>
                                    </v-list-item>

                                    <v-divider class="my-2"></v-divider>

                                    <entity-delete-dialog
                                        v-if="quickActionSnapshot"
                                        entity-type="snapshot"
                                        :snapshotData="[quickActionSnapshot]"
                                        @deleting="snapshotActionsMenu = false">
                                        <template v-slot:activator="{ attrs, open }">
                                            <v-list-item v-bind="attrs" :disabled="isInstanceArchived && !isSpaceAdmin" @click.stop="open">
                                                <v-list-item-icon>
                                                    <v-icon color="error">mdi-trash-can-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="error--text mr-4">Delete snapshot</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </entity-delete-dialog>
                                    <v-list-item v-else disabled>
                                        <v-list-item-icon>
                                            <v-icon color="grey">mdi-trash-can-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Delete snapshot</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </v-list-item-action>
                    </v-list-item>
                </template>
                <span>{{ snapshotName }}</span>
            </v-tooltip>
        </template>

        <v-list nav dense>
            <template v-if="currentState.length && isInstanceEditor && !isDistributedInstance">
                <v-list-item
                    :input-value="isCurrentStateSelected"
                    class="primary--text"
                    active-class="primary--text"
                    @click="openSnapshot(currentState[0].snid)">
                    <v-list-item-icon>
                        <v-icon color="primary">mdi-record-circle-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title :class="{ 'text--disabled': isSpaceRested || isSpaceArchived }">
                            <span v-if="isSpaceRested">Resting</span>
                            <span v-else-if="isSpaceArchived">Archived</span>
                            <span v-else>
                                <b>{{ currentState[0].long_id }}</b>
                            </span>
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>

                <v-divider class="my-2"></v-divider>
            </template>

            <v-list-item class="px-0">
                <v-list-item-title>
                    <v-text-field
                        v-model="snapshotSearch"
                        placeholder="Filter snapshots..."
                        background-color="grey lighten-4"
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        dense></v-text-field>
                </v-list-item-title>
            </v-list-item>

            <v-list-item>
                <v-list-item-title>SNAPSHOTS</v-list-item-title>
            </v-list-item>

            <template v-if="sortedSnapshots.length">
                <v-list-item
                    v-for="item in sortedSnapshots"
                    :key="item.snid"
                    :input-value="selectedSnapshot && selectedSnapshot.snid.toString() === item.snid.toString()"
                    active-class="primary--text"
                    @click="openSnapshot(item.snid)">
                    <v-list-item-icon>
                        <v-icon :color="dotColor(item)">mdi-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title :class="{ 'text--disabled': isSpaceRested || isSpaceArchived }">
                            {{ item.long_id }}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </template>

            <v-list-item v-else-if="snapshotSearch && sortedSnapshots.length === 0">
                <v-list-item-title>
                    <dashboard-empty icon="mdi-information-outline">
                        No matching snapshots found.
                        <br />
                        Try a different filter.
                    </dashboard-empty>
                </v-list-item-title>
            </v-list-item>

            <template v-else-if="isDevelopment && isInstanceEditor && !isSpaceArchived">
                <v-list-item>
                    <v-list-item-title>
                        <dashboard-empty icon="mdi-information-outline">Looks like you have no snapshots yet.</dashboard-empty>
                    </v-list-item-title>
                </v-list-item>
            </template>

            <v-divider class="my-2"></v-divider>
            <v-list-item class="px-0">
                <v-list-item-title>
                    <v-btn color="grey lighten-4" block @click="goToSnapshots()">View all snapshots</v-btn>
                </v-list-item-title>
            </v-list-item>
        </v-list>
    </v-menu>
</template>

<script>
import { isCurrentState } from '@/utils'
import { mapGetters, mapState } from 'vuex'
import { orderBy } from 'lodash'
import { DateTime } from 'luxon'
import { snapshotEnums } from '@/mixins/snapshot'
import { enumsData } from '@/mixins/enums'

const DashboardEmpty = () => import('@/components/DashboardEmpty.vue')
const EntityDeleteDialog = () => import('@/components/EntityDeleteDialog.vue')
const SnapshotRestoreDialog = () => import('@/modules/snapshot/components/TheSnapshotRestore.vue')
const unixEpoch = DateTime.fromMillis(0).toISO()

export default {
    name: 'SnapshotMenu',
    components: {
        DashboardEmpty,
        EntityDeleteDialog,
        SnapshotRestoreDialog
    },
    mixins: [snapshotEnums, enumsData],
    props: {
        collapsed: {
            type: Boolean,
            default: false
        },
        showQuickActions: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            snapshotMenu: false,
            snapshotSearch: '',
            snapshotActionsMenu: false,
            isMenuActivatorHovered: false
        }
    },
    computed: {
        ...mapGetters('instanceStore', ['isInstanceEditor', 'isDistributedInstance', 'isInstanceArchived']),
        ...mapState('instanceStore', ['instanceSnapshots', 'fetchingSnapshots']),
        ...mapState('orgStore', ['fetchingSpaces']),
        ...mapState('spaceStore', ['fetchingInstances']),
        ...mapGetters('snapshotStore', ['isDevelopment']),
        ...mapGetters('spaceStore', [
            'isSpaceArchived',
            'isSpaceRested',
            'isTrialSpace',
            'isSpaceAdmin',
            'isSpaceInAnyRestState',
            'isSpaceInAnyRestStateExceptPreResting'
        ]),
        snapshotName() {
            if (this.isSpaceArchived && this.isDevelopment) {
                return 'Archived'
            }
            if (this.isSpaceRested && this.isDevelopment) {
                return 'Resting'
            }
            if (this.$route.params.snid !== undefined && this.instanceSnapshots && this.instanceSnapshots.length) {
                return this.$store.getters['snapshotStore/snapshotLongNameById'](this.$route.params.snid)
            }
            return 'select snapshot'
        },
        sortedSnapshots() {
            const snapshots = orderBy(this.instanceSnapshots, snapshot => snapshot.snapshot_timestamp || unixEpoch, 'desc').filter(
                snapshot => !isCurrentState(snapshot.short_id)
            )

            if (this.snapshotSearch) {
                return snapshots.filter(snapshot => snapshot.long_id.toLowerCase().indexOf(this.snapshotSearch.toLowerCase()) !== -1)
            }
            return snapshots.slice(0, 5)
        },
        activeSnapshotSnid() {
            return this.$route.params.snid
        },
        currentState() {
            return this.instanceSnapshots.filter(snapshot => snapshot.long_id === 'Current State')
        },
        selectedSnapshot() {
            if (this.$route.params.snid === undefined || !this.instanceSnapshots.length) {
                return null
            }
            return this.instanceSnapshots.find(snapshot => snapshot.snid.toString() === this.$route.params.snid.toString()) || null
        },
        isCurrentStateSelected() {
            return Boolean(this.currentState.length && this.$route.params.snid && this.currentState[0].snid.toString() === this.$route.params.snid.toString())
        },
        selectedSnapshotIndicator() {
            if (!this.selectedSnapshot && !this.isCurrentStateSelected) {
                return {
                    icon: 'mdi-circle',
                    color: 'secondary'
                }
            }
            return this.getSnapshotIndicator(this.selectedSnapshot, { isCurrentState: this.isCurrentStateSelected })
        },
        isConfigRoute() {
            return (this.$route.name || '').startsWith('config')
        },
        showSnapshotMenu() {
            if (this.isConfigRoute && this.$route.params.iid !== undefined && !this.fetchingSnapshots && !this.fetchingInstances && !this.fetchingSpaces) {
                return true
            }
            if (this.instanceSnapshots.length && !this.fetchingSnapshots && !this.fetchingInstances && !this.fetchingSpaces) {
                return true
            }
            return false
        },
        quickActionSnapshot() {
            if (!this.selectedSnapshot || this.isCurrentStateSelected) {
                return null
            }
            return this.selectedSnapshot
        },
        canRestoreToCurrent() {
            return (
                this.isInstanceEditor &&
                !this.isDistributedInstance &&
                !this.isSpaceInAnyRestStateExceptPreResting &&
                !this.isSpaceArchived &&
                !this.isSpaceRested
            )
        },
        canRestoreToAnother() {
            return this.isInstanceEditor && !this.isDistributedInstance && !this.isSpaceArchived && !this.isSpaceRested
        }
    },
    methods: {
        openSnapshot(snid) {
            this.snapshotMenu = false
            this.$router.push({
                name: 'snapshot-overview',
                params: { oid: this.$route.params.oid, sid: this.$route.params.sid, iid: this.$route.params.iid, snid }
            })
        },
        goToSnapshots() {
            this.snapshotMenu = false
            this.$router.push({
                name: 'instance-snapshots',
                params: { ...this.$route.params }
            })
        }
    },
    watch: {
        '$route.params.iid': {
            immediate: true,
            handler(iid) {
                if (this.isConfigRoute && iid !== undefined) {
                    this.$store.dispatch('instanceStore/fetchInstanceSnapshots', iid)
                }
            }
        },
        '$route.name'() {
            if (this.isConfigRoute && this.$route.params.iid !== undefined) {
                this.$store.dispatch('instanceStore/fetchInstanceSnapshots', this.$route.params.iid)
            }
        }
    }
}
</script>
